import React, { useState } from "react";
import { LLMMealPlanResultOutgoing } from "../../types";
import { exportToJSON, exportToCSV, exportToStyledPDF, convertLLMMealPlanToMealPlanData } from "../../utils/exportUtils";
import { PrintableMealPlan } from "../PrintableMealPlan";

interface ExportContentProps {
    mealPlanData: LLMMealPlanResultOutgoing;
    mealPlanName: string;
    hasUnsavedChanges: boolean;
}

export const ExportContent: React.FC<ExportContentProps> = ({
    mealPlanData,
    mealPlanName,
    hasUnsavedChanges,
}) => {
    const [downloading, setDownloading] = useState(false);
    const [downloadFormat, setDownloadFormat] = useState<'json' | 'csv' | 'pdf' | null>(null);
    const [showPrintableView, setShowPrintableView] = useState(false);

    const handleDownload = async (format: 'json' | 'csv' | 'pdf') => {
        if (downloading || hasUnsavedChanges) return;

        try {
            setDownloading(true);
            setDownloadFormat(format);

            // Export based on selected format
            switch (format) {
                case 'json':
                    await exportToJSON(mealPlanData);
                    break;
                case 'csv':
                    await exportToCSV(mealPlanData);
                    break;
                case 'pdf':
                    await exportToStyledPDF(mealPlanData);
                    break;
            }
        } catch (error) {
            console.error('Error downloading meal plan:', error);
        } finally {
            setDownloading(false);
            setDownloadFormat(null);
        }
    };

    const containerStyle: React.CSSProperties = {
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "40px",
        maxWidth: "800px",
        margin: "0 auto",
        fontFamily: "system-ui, -apple-system, sans-serif",
    };

    const headerStyle: React.CSSProperties = {
        textAlign: "center",
        marginBottom: "48px",
    };

    const titleStyle: React.CSSProperties = {
        fontSize: "32px",
        fontWeight: "600",
        color: "#1f2937",
        marginBottom: "12px",
    };

    const subtitleStyle: React.CSSProperties = {
        fontSize: "16px",
        color: "#6b7280",
    };

    const warningStyle: React.CSSProperties = {
        backgroundColor: "#fef3c7",
        border: "1px solid #fbbf24",
        borderRadius: "8px",
        padding: "16px",
        marginBottom: "32px",
        display: "flex",
        alignItems: "center",
        gap: "12px",
    };

    const warningIconStyle: React.CSSProperties = {
        fontSize: "24px",
        color: "#f59e0b",
    };

    const warningTextStyle: React.CSSProperties = {
        fontSize: "16px",
        color: "#92400e",
        fontWeight: "500",
    };

    const cardsContainerStyle: React.CSSProperties = {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
        gap: "24px",
        marginBottom: "32px",
    };

    const cardStyle: React.CSSProperties = {
        border: "2px solid #e5e7eb",
        borderRadius: "12px",
        padding: "32px",
        textAlign: "center",
        cursor: downloading || hasUnsavedChanges ? "not-allowed" : "pointer",
        transition: "all 0.3s ease",
        backgroundColor: "white",
        position: "relative",
        overflow: "hidden",
        opacity: hasUnsavedChanges ? 0.6 : 1,
    };

    const iconStyle: React.CSSProperties = {
        fontSize: "48px",
        marginBottom: "16px",
        display: "block",
    };

    const formatTitleStyle: React.CSSProperties = {
        fontSize: "20px",
        fontWeight: "600",
        color: "#1f2937",
        marginBottom: "8px",
    };

    const formatDescriptionStyle: React.CSSProperties = {
        fontSize: "14px",
        color: "#6b7280",
        lineHeight: "1.5",
    };

    const downloadingOverlayStyle: React.CSSProperties = {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10,
    };

    const spinnerStyle: React.CSSProperties = {
        width: "40px",
        height: "40px",
        border: "4px solid #f3f4f6",
        borderRadius: "50%",
        borderTopColor: "#3b82f6",
        animation: "spin 1s linear infinite",
    };

    const noteStyle: React.CSSProperties = {
        backgroundColor: "#f0f9ff",
        border: "1px solid #bae6fd",
        borderRadius: "8px",
        padding: "16px",
        marginTop: "32px",
        textAlign: "center",
    };

    const noteTextStyle: React.CSSProperties = {
        fontSize: "14px",
        color: "#0369a1",
        margin: 0,
    };

    return (
        <>
            <style>
                {`
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                `}
            </style>
            <div style={containerStyle}>
                <div style={headerStyle}>
                    <h1 style={titleStyle}>📥 Export Your Meal Plan</h1>
                    <p style={subtitleStyle}>
                        Download "{mealPlanName}" in your preferred format
                    </p>
                </div>

                {hasUnsavedChanges && (
                    <div style={warningStyle}>
                        <span style={warningIconStyle}>⚠️</span>
                        <span style={warningTextStyle}>
                            Please save your changes before exporting. Export options are disabled while you have unsaved changes.
                        </span>
                    </div>
                )}

                <div style={cardsContainerStyle}>
                    {/* Printable View */}
                    <div
                        style={cardStyle}
                        onClick={() => !hasUnsavedChanges && setShowPrintableView(true)}
                        onMouseEnter={(e) => {
                            if (!downloading && !hasUnsavedChanges) {
                                e.currentTarget.style.transform = "translateY(-4px)";
                                e.currentTarget.style.boxShadow = "0 10px 25px rgba(0, 0, 0, 0.1)";
                                e.currentTarget.style.borderColor = "#8b5cf6";
                            }
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "none";
                            e.currentTarget.style.borderColor = "#e5e7eb";
                        }}
                    >
                        <span style={iconStyle}>🖨️</span>
                        <h3 style={formatTitleStyle}>Printable View</h3>
                        <p style={formatDescriptionStyle}>
                            Simple grid layout optimized for printing
                        </p>
                    </div>

                    {/* JSON Export */}
                    <div
                        style={cardStyle}
                        onClick={() => !hasUnsavedChanges && handleDownload('json')}
                        onMouseEnter={(e) => {
                            if (!downloading && !hasUnsavedChanges) {
                                e.currentTarget.style.transform = "translateY(-4px)";
                                e.currentTarget.style.boxShadow = "0 10px 25px rgba(0, 0, 0, 0.1)";
                                e.currentTarget.style.borderColor = "#3b82f6";
                            }
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "none";
                            e.currentTarget.style.borderColor = "#e5e7eb";
                        }}
                    >
                        <span style={iconStyle}>📋</span>
                        <h3 style={formatTitleStyle}>JSON Format</h3>
                        <p style={formatDescriptionStyle}>
                            Complete data structure for developers and advanced users
                        </p>
                        {downloading && downloadFormat === 'json' && (
                            <div style={downloadingOverlayStyle}>
                                <div style={spinnerStyle} />
                                <p style={{ marginTop: "16px", color: "#6b7280", fontSize: "14px" }}>
                                    Preparing download...
                                </p>
                            </div>
                        )}
                    </div>

                    {/* CSV Export */}
                    <div
                        style={cardStyle}
                        onClick={() => !hasUnsavedChanges && handleDownload('csv')}
                        onMouseEnter={(e) => {
                            if (!downloading && !hasUnsavedChanges) {
                                e.currentTarget.style.transform = "translateY(-4px)";
                                e.currentTarget.style.boxShadow = "0 10px 25px rgba(0, 0, 0, 0.1)";
                                e.currentTarget.style.borderColor = "#10b981";
                            }
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "none";
                            e.currentTarget.style.borderColor = "#e5e7eb";
                        }}
                    >
                        <span style={iconStyle}>📊</span>
                        <h3 style={formatTitleStyle}>CSV Spreadsheet</h3>
                        <p style={formatDescriptionStyle}>
                            Import into Excel or Google Sheets for analysis
                        </p>
                        {downloading && downloadFormat === 'csv' && (
                            <div style={downloadingOverlayStyle}>
                                <div style={spinnerStyle} />
                                <p style={{ marginTop: "16px", color: "#6b7280", fontSize: "14px" }}>
                                    Preparing download...
                                </p>
                            </div>
                        )}
                    </div>

                    {/* PDF Export */}
                    <div
                        style={cardStyle}
                        onClick={() => !hasUnsavedChanges && handleDownload('pdf')}
                        onMouseEnter={(e) => {
                            if (!downloading && !hasUnsavedChanges) {
                                e.currentTarget.style.transform = "translateY(-4px)";
                                e.currentTarget.style.boxShadow = "0 10px 25px rgba(0, 0, 0, 0.1)";
                                e.currentTarget.style.borderColor = "#ef4444";
                            }
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "none";
                            e.currentTarget.style.borderColor = "#e5e7eb";
                        }}
                    >
                        <span style={iconStyle}>📄</span>
                        <h3 style={formatTitleStyle}>PDF Document</h3>
                        <p style={formatDescriptionStyle}>
                            Print-friendly format with complete meal details
                        </p>
                        {downloading && downloadFormat === 'pdf' && (
                            <div style={downloadingOverlayStyle}>
                                <div style={spinnerStyle} />
                                <p style={{ marginTop: "16px", color: "#6b7280", fontSize: "14px" }}>
                                    Preparing download...
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                <div style={noteStyle}>
                    <p style={noteTextStyle}>
                        💡 <strong>Tip:</strong> All formats include macro nutrition information, ingredients, and meal details.
                    </p>
                </div>
            </div>

            {/* Printable View Modal */}
            {showPrintableView && (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    zIndex: 1000,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "20px",
                }}>
                    <div style={{
                        backgroundColor: "white",
                        borderRadius: "12px",
                        width: "90vw",
                        height: "90vh",
                        overflow: "auto",
                        padding: "20px",
                        position: "relative",
                    }}>
                        <PrintableMealPlan
                            mealPlanData={convertLLMMealPlanToMealPlanData(mealPlanData)}
                            onClose={() => setShowPrintableView(false)}
                        />
                    </div>
                </div>
            )}
        </>
    );
}; 